// Omnix/client/ui/DynamicComposer.js
import { ComponentBase } from "../core/ComponentBase.js";
import 'https://cdn.jsdelivr.net/npm/emoji-picker-element@^1/index.js';

export class DynamicComposer extends ComponentBase {
    static styles = `
        .dynamic-composer-wrapper {
            padding: 0.75rem 1rem;
            border-top: 1px solid var(--bs-border-color);
            background-color: var(--bs-secondary-bg);
            flex-shrink: 0;
        }

        .dynamic-composer-wrapper.disabled {
            position: relative;
        }
        
        .disabled-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(var(--bs-tertiary-bg-rgb), 0.7);
            display: flex;
            align-items: center;
            justify-content: center;
            text-align: center;
            padding: 1rem;
            font-size: 0.9rem;
            font-weight: 500;
            color: var(--bs-secondary-color);
            border-radius: var(--bs-border-radius);
            z-index: 10;
        }

        .chat-composer-area {
            display: flex;
            align-items: flex-end;
            gap: 0.75rem;
            background-color: var(--bs-tertiary-bg);
            border-radius: 24px;
            padding: 0.25rem 0.5rem;
            border: 1px solid var(--bs-border-color-translucent);
        }
        .chat-composer-area:focus-within {
            border-color: var(--bs-primary);
            box-shadow: 0 0 0 0.2rem rgba(var(--bs-primary-rgb, 0,123,255), 0.25);
        }
        .chat-composer-actions {
            display: flex;
            align-items: center;

        }
        .chat-composer-actions .btn {
            color: var(--bs-secondary-color);
        }
        .chat-composer-actions .btn:hover {
            color: var(--bs-primary);
        }
        .chat-composer-textarea {
            flex-grow: 1;
            border: none;
            outline: none;
            resize: none;
            background: transparent;
            padding: 0.6rem 0.2rem;
            max-height: 150px;
            min-height: 24px;
            line-height: 1.5;
            color: var(--bs-body-color);
        }
        .attachment-previews {
            display: flex;
            flex-wrap: wrap;
            gap: 0.5rem;
            padding: 0.5rem;
        }
        .attachment-preview-item {
            background-color: var(--bs-secondary-bg-subtle);
            color: var(--bs-secondary-text-emphasis);
            font-size: 0.8rem;
        }
        .email-composer-actions {
            display: flex;
            gap: 0.5rem;
            justify-content: center;
        }
    `;

    constructor(config) {
        super(config);
        this.activeConversation = null;
        this.attachments = [];
        this.isDisabled = false; // NEW
        this.disabledReason = ""; // NEW

        this.applyStyles();
    }

    setConversation(conversation) {
        this.activeConversation = conversation;
        this.attachments = []; // Clear attachments when conversation changes
        if (this.container) this.render();
    }

    render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'dynamic-composer-wrapper';
        }
        this.container.innerHTML = '';

        if (this.isDisabled) {
            const overlay = document.createElement('div');
            overlay.className = 'disabled-overlay';
            overlay.textContent = this.disabledReason;
            this.container.appendChild(overlay);
            this.container.classList.add('disabled');
        } else {
            this.container.classList.remove('disabled');
        }


        if (!this.activeConversation) {
            this.container.innerHTML = `<div class="text-muted text-center small">${this.t('Select a conversation to reply.')}</div>`;
            return this.container;
        }

        if (this.activeConversation.channelType === 'voice') {
            this.container.innerHTML = `<div class="text-muted text-center small p-2">${this.t('This is a voice call log. Replies are not available.')}</div>`;
            return this.container;
        }

        // Render based on channel type
        if (this.activeConversation.channelType === 'email') {
            this.renderEmailActions();
        } else {
            this.renderChatActions();
        }

        return this.container;
    }

    renderEmailActions() {
        const div = document.createElement('div');
        div.className = 'email-composer-actions';
        const isNewConversation = !this.activeConversation || !this.activeConversation.lastInteractionSnippet;

        if (isNewConversation) {
            div.innerHTML = `
                <button class="btn btn-primary" data-action="compose">
                    <i class="bi bi-pencil-square me-2"></i> ${this.t('Compose Email')}
                </button>
            `;
        } else {
            div.innerHTML = `
                <button class="btn btn-secondary" data-action="reply"><i class="bi bi-reply-fill me-2"></i> ${this.t('Reply')}</button>
                <button class="btn btn-secondary" data-action="replyAll"><i class="bi bi-reply-all-fill me-2"></i> ${this.t('Reply All')}</button>
                <button class="btn btn-secondary" data-action="forward"><i class="bi bi-arrow-right-circle-fill me-2"></i> ${this.t('Forward')}</button>
            `;
        }

        div.addEventListener('click', (e) => {
            const action = e.target.closest('button')?.dataset.action;
            if (action) {
                this.emit('composeEmail', { action });
            }
        });
        this.container.appendChild(div);
    }

    renderChatActions() {
        const attachmentPreviews = document.createElement('div');
        attachmentPreviews.className = 'attachment-previews';
        this.container.appendChild(attachmentPreviews);

        const composerArea = document.createElement('div');
        composerArea.className = 'chat-composer-area';
        composerArea.innerHTML = `
            <div class="chat-composer-actions">
                <div class="dropup">
                    <button class="btn btn-link" type="button" data-bs-toggle="dropdown" aria-expanded="false" title="${this.t('Emoji')}"><i class="bi bi-emoji-smile"></i></button>
                    <div class="dropdown-menu p-0 border-0">
                        <emoji-picker class="light"></emoji-picker>
                    </div>
                </div>
                <input type="file" id="file-input-${this.config.instanceId}" multiple style="display: none;" />
                <button class="btn btn-link" type="button" id="attach-btn-${this.config.instanceId}" title="${this.t('Attach')}"><i class="bi bi-paperclip"></i></button>
            </div>
            <textarea class="chat-composer-textarea" placeholder="${this.t('Type a message...')}" rows="1"></textarea>
            <button class="btn btn-link send-btn" title="${this.t('Send')}"><i class="bi bi-send-fill"></i></button>
        `;
        this.container.appendChild(composerArea);

        this.textarea = composerArea.querySelector('textarea');
        this.sendButton = composerArea.querySelector('.send-btn');
        this.fileInput = composerArea.querySelector(`#file-input-${this.config.instanceId}`);

        // Event Listeners
        this.sendButton.addEventListener('click', () => this.handleSendMessage());
        this.textarea.addEventListener('keypress', (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                this.handleSendMessage();
            }
        });
        this.textarea.addEventListener('input', () => this.autoResizeTextarea());
        composerArea.querySelector(`#attach-btn-${this.config.instanceId}`).addEventListener('click', () => this.fileInput.click());
        this.fileInput.addEventListener('change', (e) => this.handleFilesSelected(e.target.files));
        composerArea.querySelector('emoji-picker').addEventListener('emoji-click', e => {
            this.textarea.value += e.detail.unicode;
            this.textarea.focus();
        });

        this.renderAttachmentPreviews();
    }

    handleFilesSelected(files) {
        for (const file of files) {
            this.attachments.push(file);
        }
        this.renderAttachmentPreviews();
        this.fileInput.value = ''; // Reset input
    }

    renderAttachmentPreviews() {
        const previewContainer = this.container.querySelector('.attachment-previews');
        if (!previewContainer) return;
        previewContainer.innerHTML = '';
        this.attachments.forEach((file, index) => {
            const preview = document.createElement('div');
            preview.className = 'attachment-preview-item badge';
            preview.innerHTML = `
                <i class="bi bi-file-earmark-text me-1"></i>
                <span>${file.name}</span>
                <button type="button" class="btn-close ms-1" data-index="${index}"></button>
            `;
            preview.querySelector('.btn-close').addEventListener('click', () => {
                this.attachments.splice(index, 1);
                this.renderAttachmentPreviews();
            });
            previewContainer.appendChild(preview);
        });
    }

    handleSendMessage() {
        const text = this.textarea.value.trim();
        if (!text && this.attachments.length === 0) return;

        this.emit('messageSendRequested', {
            channelType: this.activeConversation.channelType,
            content: { text },
            attachments: this.attachments
        });

        this.textarea.value = '';
        this.attachments = [];
        this.renderAttachmentPreviews();
        this.autoResizeTextarea();
    }

    autoResizeTextarea() {
        this.textarea.style.height = 'auto';
        requestAnimationFrame(() => {
            this.textarea.style.height = `${this.textarea.scrollHeight}px`;
        });
    }

    setDisabled(isDisabled, reason = "") {
        if (this.isDisabled !== isDisabled) {
            this.isDisabled = isDisabled;
            this.disabledReason = reason;
            if (this.container) this.render(); // Re-render to show/hide the overlay
        }
    }

}